import { HttpClient } from '@angular/common/http';
import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { ReplaySubject } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';
import { environment } from '../../../../environments/environment';
import { CampaignsApiService } from '../../../pages/campaigns/campaigns.apiservice';
import { ReportsApiService } from '../../../pages/reports/reports.apiservice';
import { DataFeedService } from '../../api/datafeed.service';
import { TranslateService } from '@ngx-translate/core';

type CampaignMetricsSummary = {
  platform: string;
  campaignName: string;
  dateStart: string;
  dateStop: string;
  graphDateRange?: string;
  currencyCode: string;
  clicks: number;
  impressions: number;
  leads: number;
  cost: number;
  ctr: number;
  cpc: number;
  cpm: number;
  lsr: number;
  cpl: number;
};

@Component({
  selector: 'app-campaign-analysis-modal',
  templateUrl: './campaign-analysis-modal.component.html',
  styleUrls: ['./campaign-analysis-modal.component.scss']
})
export class CampaignAnalysisModalComponent implements OnInit, OnDestroy {
  private destroyed$: ReplaySubject<boolean> = new ReplaySubject(1);

  @Input() campaignId: string = '';
  @Input() isAdmin: boolean = false;
  @Input() dateStart: string | null = null;
  @Input() dateStop: string | null = null;

  get hasAnalysisDateRange(): boolean {
    return !!this.dateStart && !!this.dateStop;
  }

  get analysisDateRange(): string {
    return this.hasAnalysisDateRange ? `${this.dateStart} → ${this.dateStop}` : '';
  }

  campaignAnalysis: string = '';
  campaignAnalysisLoading: boolean = false;
  campaignAnalysisSimplifying: boolean = false;
  campaignAnalysisSimplified: boolean = false;
  recentNotes: Array<any> = [];

  campaignName: string = '';
  platform: string = '';

  metricsLoading: boolean = false;
  metricsError: string = '';
  notesError: string = '';
  metrics: CampaignMetricsSummary | null = null;

  adminPrompt: string = '';
  adminMetricsBlock: string = '';
  adminNotesBlock: string = '';
  adminSystemContent: string = '';
  adminTemperature: number = 1.0;
  adminModel: 'gpt-5-mini' | 'gpt-5' | 'gpt-5.6-luna' | 'gpt-5.6-terra' = 'gpt-5.6-luna';

  constructor(
    public activeModal: NgbActiveModal,
    private http: HttpClient,
    private campaignsApiService: CampaignsApiService,
    private reportsApiService: ReportsApiService,
    private dataFeedService: DataFeedService,
    private translate: TranslateService,
  ) {}

  ngOnInit(): void {
    // Intentionally do not auto-load notes/metrics.
    // We load on demand when the user clicks Generate Analysis.
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  private async loadRecentNotesForAnalysis(): Promise<void> {
    if (!this.campaignId) {
      this.recentNotes = [];
      return;
    }

    try {
      const response: any = await this.dataFeedService.getNotes(this.campaignId, 1)
        .pipe(take(1))
        .toPromise();

      // API responses in this repo typically look like { good: boolean, data: any[] }
      const notes: any[] = Array.isArray(response)
        ? response
        : (response?.good ? response.data : []);

      if (!notes || notes.length === 0) {
        this.recentNotes = [];
        return;
      }

      // Exclude internal notes from analysis
      const nonInternalNotes = notes.filter(note => {
        const isInternal = (note as any)?.isInternal;
        return !(isInternal === true || isInternal === 'true' || isInternal === 1);
      });

      if (nonInternalNotes.length === 0) {
        this.recentNotes = [];
        return;
      }

      const { dateStart, dateStop } = this.getAnalysisDateRange();
      const rangeStart = new Date(`${dateStart}T00:00:00`);
      const rangeStop = new Date(`${dateStop}T23:59:59.999`);

      const recentNotes = nonInternalNotes.filter(note => {
        const noteDate = new Date(note.updatedOn);
        return noteDate >= rangeStart && noteDate <= rangeStop;
      });

      if (recentNotes.length === 0) {
        this.notesError = this.hasAnalysisDateRange
          ? this.translate.instant('NO_NOTES_FOUND_IN_DATE_RANGE', { dateStart: this.dateStart, dateStop: this.dateStop })
          : this.translate.instant('NO_NOTES_FOUND_IN_LAST_30_DAYS');
      } else {
        this.notesError = '';
      }
      this.recentNotes = recentNotes.length > 0 ? recentNotes : [];
    } catch (err) {
      console.error('Error loading campaign notes:', err);
      this.recentNotes = [];
    }
  }

  private getAnalysisDateRange(): { dateStart: string; dateStop: string } {
    if (this.dateStart && this.dateStop) {
      return {
        dateStart: this.dateStart,
        dateStop: this.dateStop
      };
    }

    const dateStop = new Date();
    const dateStart = new Date();
    dateStart.setDate(dateStart.getDate() - 30);

    // Keep consistent with other codepaths in this repo
    return {
      dateStart: dateStart.toISOString().split('T')[0],
      dateStop: dateStop.toISOString().split('T')[0]
    };
  }

  private async loadCampaignMetricsForAnalysis(): Promise<void> {
    if (!this.campaignId) {
      this.metrics = null;
      this.metricsError = '';
      this.notesError = '';
      this.metricsLoading = false;
      return;
    }

    this.metricsLoading = true;
    this.metricsError = '';
    this.notesError = '';
    this.metrics = null;

    const { dateStart, dateStop } = this.getAnalysisDateRange();
    const performanceMetricsCount = '7';

    try {
      const campaignResponse: any = await this.campaignsApiService.getCampaignDetails(this.campaignId)
        .pipe(take(1))
        .toPromise();

      this.platform = campaignResponse?.data?.platform || '';
      this.campaignName = campaignResponse?.data?.name || '';

      const normalizedPlatform = this.platform.trim().toLowerCase();
      if (normalizedPlatform !== 'google' && normalizedPlatform !== 'facebook' && normalizedPlatform !== 'meta') {
        this.metricsError = this.translate.instant('METRICS_ARE_NOT_AVAILABLE_FOR_PLATFORM', { platform: this.platform || this.translate.instant('UNKNOWN') });
        this.metrics = null;
        return;
      }

      const data: any = await this.reportsApiService.getRawCampaignsData(
        this.campaignId,
        this.platform,
        dateStart,
        dateStop,
        performanceMetricsCount
      )
        .pipe(take(1))
        .toPromise();

      if (!data || data.ErrorMessage || !data.Platform) {
        this.metricsError = this.translate.instant('NO_METRICS_DATA_FOUND_FOR_THE_SELECTED_DATE_RANGE');
        this.metrics = null;
        return;
      }

      const overview = this.extractOverviewMetricsFromRawData(data);
      this.metrics = {
        platform: (data.Platform || this.platform || '').trim(),
        campaignName: this.campaignName,
        dateStart,
        dateStop,
        graphDateRange: (data.GraphDateRange || '').trim(),
        currencyCode: (data.CurrencyCode || 'USD').toUpperCase(),
        ...overview
      };
    } catch (err) {
      console.error('Error loading campaign metrics:', err);
      this.metricsError = this.translate.instant('ERROR_FETCHING_CAMPAIGN_METRICS');
      this.metrics = null;
    } finally {
      this.metricsLoading = false;
    }
  }

  private extractOverviewMetricsFromRawData(data: any): Omit<CampaignMetricsSummary, 'platform' | 'campaignName' | 'dateStart' | 'dateStop' | 'graphDateRange' | 'currencyCode'> {
    const clicks = data?.Clicks > 0 ? data.Clicks : 0;
    const impressions = data?.Impressions > 0 ? data.Impressions : 0;
    const leads = data?.Conversions > 0 ? data.Conversions : 0;
    const cost = data?.Cost > 0 ? data.Cost : 0;

    const ctr = impressions > 0 ? clicks / impressions : 0;
    const cpc = clicks > 0 ? cost / clicks : 0;
    const cpm = impressions > 0 ? (cost / impressions) * 1000 : 0;
    const lsr = clicks > 0 ? leads / clicks : 0;
    const cpl = leads > 0 ? cost / leads : 0;

    return { clicks, impressions, leads, cost, ctr, cpc, cpm, lsr, cpl };
  }

  async generateCampaignAnalysis(): Promise<void> {
    this.campaignAnalysisLoading = true;
    this.campaignAnalysis = '';
    this.campaignAnalysisSimplified = false;

    // Always refresh context (notes + metrics) at click-time.
    await Promise.all([
      this.loadRecentNotesForAnalysis(),
      this.loadCampaignMetricsForAnalysis()
    ]);

    const hasNotes = this.recentNotes.length > 0;
    const hasMetrics = !!this.metrics;

    if (!hasNotes && !hasMetrics) {
      this.campaignAnalysisLoading = false;
      this.campaignAnalysis = `<p class="text-warning">${this.translate.instant('NO_NOTES_OR_METRICS_FOUND_FOR_THIS_CAMPAIGN_TO_ANALYZE')}</p>`;
      this.metricsError = '';
      this.notesError = '';
      return;
    }

    const notesText = hasNotes
      ? this.recentNotes.map(note => {
          return `Date: ${note.updatedOn || note.createdOn}\nAuthor: ${note.assignedUserName}\nTitle: ${note.title}\nContent: ${note.description}`;
        }).join('\n\n---\n\n')
      : '';

    this.callBackendForAnalysis(notesText, this.metrics);
  }

  resendAdminCampaignAnalysis(): void {
    if (!this.isAdmin || this.campaignAnalysisLoading) return;

    const prompt = [this.adminPrompt, this.adminMetricsBlock, this.adminNotesBlock]
      .filter(part => part && part.trim())
      .join('\n\n---\n\n');

    if (!prompt.trim()) return;

    const requestedTemperature = Number(this.adminTemperature);
    this.adminTemperature = Number.isFinite(requestedTemperature)
      ? Math.min(Math.max(requestedTemperature, 0), 1.4)
      : 1.0;

    this.campaignAnalysisLoading = true;
    this.campaignAnalysis = '';
    this.campaignAnalysisSimplified = false;
    this.sendAnalysisRequest(prompt, this.adminSystemContent, this.adminTemperature, this.adminModel);
  }

  simplifyCampaignAnalysis(): void {
    if (!this.campaignAnalysis || this.campaignAnalysisSimplifying || this.campaignAnalysisSimplified) return;

    const apiBaseUrl = environment.apiBaseUrl?.trim();
    if (!apiBaseUrl) {
      return;
    }

    const analysisText = this.campaignAnalysis
      .replace(/<[^>]*>/g, ' ')
      .replace(/&nbsp;/g, ' ')
      .replace(/&amp;/g, '&')
      .replace(/\s+/g, ' ')
      .trim();

    if (!analysisText) return;

    const currentLanguage = this.translate.currentLang || this.translate.defaultLang || 'en-US';
    const responseLanguage = /^en(?:-|$)/i.test(currentLanguage) ? '' : `Respond entirely in ${currentLanguage}. `;
    const requestBody = {
      model: this.adminModel,
      messages: [
        {
          role: 'system',
          content: 'You simplify digital marketing campaign analyses. Return only the simplified analysis.'
        },
        {
          role: 'user',
          content: `${responseLanguage}Rewrite the following campaign analysis in simpler, clearer language while preserving all important facts, metrics, and conclusions. Remove the sections and don't give the response a new title. Summarize the whole analysis into one or two short paragraphs. Use a professional but easy-to-understand tone. Do not add recommendations or information that is not already present.\n\nCampaign analysis:\n${analysisText}`
        }
      ],
      max_completion_tokens: 2000,
      temperature: 0.3
    };

    this.campaignAnalysisSimplifying = true;
    this.http.post(`${apiBaseUrl}/api/chat/gpt5`, requestBody)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: (response: any) => {
          const rawContent = response?.reply || response?.choices?.[0]?.message?.content;
          if (rawContent && String(rawContent).trim()) {
            this.campaignAnalysis = this.formatAnalysisResponse(String(rawContent).trim());
            this.campaignAnalysisSimplified = true;
          }
          this.campaignAnalysisSimplifying = false;
        },
        error: () => {
          this.campaignAnalysisSimplifying = false;
        }
      });
  }

  private callBackendForAnalysis(notesText: string, metrics: CampaignMetricsSummary | null): void {
    const promptParts = this.buildPromptParts(notesText, metrics);
    this.adminPrompt = promptParts.prompt;
    this.adminMetricsBlock = promptParts.metricsBlock;
    this.adminNotesBlock = promptParts.notesBlock;
    this.adminSystemContent = this.getDefaultAnalysisSystemContent();
    this.adminTemperature = 1.0;
    this.adminModel = 'gpt-5.6-luna';
    this.sendAnalysisRequest([promptParts.prompt, promptParts.metricsBlock, promptParts.notesBlock].join('\n\n---\n\n'));
  }

  private getDefaultAnalysisSystemContent(): string {
    return 'You are a digital marketing campaign analyst. Respond in a casual and friendly way, and act as if you are the one running the campaign. Follow the user instructions exactly. Benchmark values are internal evaluation criteria only and must never appear in the response. Do not mention, quote, paraphrase, or compare against any benchmark, target, threshold, cutoff, or goal unless the user explicitly asks for it.';
  }

  private buildPromptParts(notesText: string, metrics: CampaignMetricsSummary | null): { prompt: string; metricsBlock: string; notesBlock: string } {
    const campaignPlatform = metrics?.platform || 'Unknown';
    const currentLanguage = this.translate.currentLang || this.translate.defaultLang || 'en-US';
    const responseLanguage = /^en(?:-|$)/i.test(currentLanguage) ? '' : 'Respond entirely in ' + currentLanguage + ', including the section headers. ';
    const usesConversionMetrics = /\b(conversion actions?|conversion rate|cpa)\b/i.test(notesText);
    const outcomeLabel = usesConversionMetrics ? 'Conversions' : 'Leads';
    const efficiencyMetric = usesConversionMetrics ? 'CPA (Cost Per Action)' : 'CPL (Cost Per Lead)';
    const rateMetric = usesConversionMetrics ? 'Conversion Rate' : 'LSR (Lead Submission Rate)';
    const explainMetrics = usesConversionMetrics
      ? 'Explain the campaign performance using the conversion metrics and their implications. This campaign does not measure leads; use Conversion Rate and do not refer to leads or LSR.'
      : 'Explain the campaign performance using the lead metrics and their implications. LSR always means Lead Submission Rate.';
    const normalizedPlatform = campaignPlatform.trim().toLowerCase();
    const conversionPath = usesConversionMetrics
      ? 'landing pages'
      : normalizedPlatform === 'facebook' || normalizedPlatform === 'meta'
      ? 'lead forms'
      : normalizedPlatform === 'google'
        ? 'landing pages'
        : 'the campaign conversion path';
    // const detailedMetrics = `Explain what ${efficiencyMetric}, ${rateMetric}, CTR (Click-Through Rate), CPC (Cost Per Click), and CPM (Cost Per Thousand Impressions) mean and why each matters. Explain that ${efficiencyMetric} measures acquisition efficiency, ${rateMetric} indicates effectiveness of the ${conversionPath}. `;
    const metricsBlock = metrics
      ? `Campaign Performance Metrics (${metrics.dateStart} to ${metrics.dateStop}):\nCampaign: ${metrics.campaignName || 'N/A'}\nPlatform: ${metrics.platform || 'N/A'}\nDate Range: ${metrics.dateStart} to ${metrics.dateStop}${metrics.graphDateRange ? ` (${metrics.graphDateRange})` : ''}\nCurrency: ${metrics.currencyCode}\nClicks: ${metrics.clicks}\nImpressions: ${metrics.impressions}\n${outcomeLabel}: ${metrics.leads}\nCost: ${metrics.cost}\nCTR: ${metrics.ctr}\nCPC: ${metrics.cpc}\nCPM: ${metrics.cpm}\n${rateMetric}: ${metrics.lsr}\n${efficiencyMetric}: ${metrics.cpl}`
      : `Campaign Performance Metrics (${this.getAnalysisDateRange().dateStart} to ${this.getAnalysisDateRange().dateStop}): Not available.`;

    const hasNotes = notesText && notesText.trim().length > 0;
    const notesBlock = notesText && notesText.trim().length > 0
      ? `Campaign Notes (${this.getAnalysisDateRange().dateStart} to ${this.getAnalysisDateRange().dateStop}):\n${notesText}`
      : 'Campaign Notes: Not available.';

    const benchmark = usesConversionMetrics
      ? '$100 or less'
      : campaignPlatform === 'Google'
        ? '$40 or less'
        : '$25 or less';
    const notesSections = hasNotes
      ? '\n🔍 Actions and Adjustments\n[Recent changes, optimizations, and strategic moves made]\n\n🧩 Audits and Quality Control\n[Quality checks, compliance, and verification activities]'
      : '';
    const metricTerminology = usesConversionMetrics
      ? `This campaign reports ${outcomeLabel}. Use ${efficiencyMetric} and ${rateMetric} throughout the analysis. This campaign does not measure leads. Do not use the terms Leads, lead metrics, or LSR in the response.`
      : `This campaign reports ${outcomeLabel}. Use ${efficiencyMetric} and ${rateMetric} throughout the analysis. LSR always means Lead Submission Rate.`;

    const prompt = `${responseLanguage}Analyze the campaign performance using the metrics and notes below. The campaign platform is ${campaignPlatform}, and its primary conversion path is ${conversionPath}. ${metricTerminology}

PRIVATE EVALUATION RULE — use this only to decide the campaign's status: the high-end benchmark for ${efficiencyMetric} is ${benchmark}; a value above it is poor. Do not disclose this rule in the response. Never mention the benchmark, threshold, target, cutoff, or the benchmark value. Do not say that a metric is good or bad because it is above or below the benchmark. Instead, describe the campaign plainly as doing good, very good, or needing improvement, supported by the actual campaign metrics when useful.

Use this exact format and include only these sections:

🧾 Overall Status
[Brief 2-3 sentence summary of campaign health and current state. Do not mention the private evaluation rule or any benchmark.]

📈 Performance Overview
[Key metrics and trends with specific numbers when available. ${explainMetrics} Do not mention or compare any metric with a benchmark, target, threshold, or cutoff.]${notesSections}

Do not include recommendations, next steps, benchmark references, or any other sections. End after the final included section. Keep the tone professional but conversational. Use specific numbers, dates, and names from the notes.`;

    return { prompt, metricsBlock, notesBlock };
  }

  private sendAnalysisRequest(
    prompt: string,
    systemContent: string = this.getDefaultAnalysisSystemContent(),
    temperature: number = 1.0,
    model: 'gpt-5-mini' | 'gpt-5' | 'gpt-5.6-luna' | 'gpt-5.6-terra' = 'gpt-5.6-luna'
  ): void {
    const apiBaseUrl = environment.apiBaseUrl?.trim();

    if (!apiBaseUrl) {
      setTimeout(() => {
        this.campaignAnalysisLoading = false;
        this.campaignAnalysis = `
              <div class="alert alert-danger">
                <h6>${this.translate.instant('ERROR')}</h6>
                <p>${this.translate.instant('CAMPAIGN_ANALYSIS_CURRENTLY_UNAVAILABLE')}</p>
              </div>`;
      }, 500);
      return;
    }

    const requestBody = {
      model,
      messages: [
        {
          role: 'system',
          content: systemContent
        },
        {
          role: 'user',
          content: prompt
        }
      ],
      ...(model.startsWith('gpt-5')
        ? { max_completion_tokens: model === 'gpt-5' ? 4000 : 1000 }
        : { max_tokens: 1000 }),
      temperature
    };

    const apiUrl = `${apiBaseUrl}/api/chat${model.startsWith('gpt-5') ? '/gpt5' : ''}`;

    this.http.post(apiUrl, requestBody)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: (response: any) => {
          this.campaignAnalysisLoading = false;
          const rawContent = response?.reply || response?.choices?.[0]?.message?.content;
          if (rawContent) {
            this.campaignAnalysis = this.formatAnalysisResponse(rawContent);
          } else {
            const responseError = response?.error;
            this.campaignAnalysis = `<p class="text-danger">${responseError || this.translate.instant('ERROR_INVALID_RESPONSE')}</p>`;
          }
        },
        error: (error) => {
          this.campaignAnalysisLoading = false;

          if (error.status === 401) {
            this.campaignAnalysis = `<p class="text-danger">${this.translate.instant('AUTHENTICATION_ERROR_CAMPAIGN_ANALYSIS_IS_CURRENTLY_UNAVAILABLE')}</p>`;
          } else if (error.status === 429) {
            this.campaignAnalysis = `
              <div class="alert alert-warning">
                <h6>${this.translate.instant('REQUEST_LIMIT_EXCEEDED')}</h6>
                <p>${this.translate.instant('REQUEST_LIMIT')}</p>
              </div>`;
          } else {
            this.campaignAnalysis = `
              <div class="alert alert-danger">
                <h6>${this.translate.instant('UNEXPECTED_ERROR')}</h6>
                <p>${this.translate.instant('AN_UNEXPECTED_ERROR_OCCURRED_WHILE_FETCHING_THE_CAMPAIGN_ANALYSIS')}</p>
              </div>`;
          }
        }
      });
  }

  private formatAnalysisResponse(content: string): string {
    let formatted = content
      .replace(/🧾 (.*?)(?=\n|$)/g, '<h5 class="analysis-header status-header">🧾 $1</h5>')
      .replace(/📈 (.*?)(?=\n|$)/g, '<h5 class="analysis-header performance-header">📈 $1</h5>')
      .replace(/🔍 (.*?)(?=\n|$)/g, '<h5 class="analysis-header actions-header">🔍 $1</h5>')
      .replace(/🧩 (.*?)(?=\n|$)/g, '<h5 class="analysis-header audits-header">🧩 $1</h5>')
      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
      .replace(/^- (.*?)(?=\n- |\n\n|$)/gm, '<li>$1</li>')
      .replace(/(<li>.*<\/li>)/g, '<ul class="analysis-list">$1</ul>')
      .replace(/\n\n/g, '</p><p class="analysis-paragraph">')
      .replace(/^(.*)$/, '<p class="analysis-paragraph">$1</p>')
      .replace(/<p class="analysis-paragraph"><\/p>/g, '')
      .replace(/<\/h5>/g, '</h5><div class="analysis-section">')
      .replace(/<h5 class="analysis-header/g, '</div><h5 class="analysis-header')
      + '</div>';

    return `<div class="formatted-analysis">${formatted}</div>`;
  }

}
